import { useCallback, useEffect, useState } from 'react';
import { Link } from '../components/Link.tsx';
import { Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Sel {
  selection: string;
  rounds: number;
  hits: number;
  hitRate: number;
  hitRateCi: [number, number];
  avgOdds: number;
  playerReturn: number;
  playerReturnCi: [number, number];
  fairOdds: number | null;
  suggestedOdds: number | null;
}
interface Recent {
  gameRoundId: string;
  at: string;
  camera: string | null;
  city: string | null;
  country: string | null;
  low: number | null;
  high: number | null;
  median: number | null;
  windows: number | null;
  finalCount: number;
  winning: string[];
  lines: { selection: string; odds: number; p: number | null; edge: number | null }[];
}
interface Insights {
  hours: number;
  edgeBps: number;
  rounds: number;
  oddsSource: string;
  outcome: { below: number; inside: number; above: number; avgCountMinusMedian: number | null };
  selections: Sel[];
  recent: Recent[];
  policy: { minWindows: number; minHourWindows: number; edgeBps: number };
  readiness: { sourceKey: string; camera: string | null; city: string | null; country: string | null; windows: number; hoursOk: number; priceableModels: number }[];
}

const LABEL: Record<string, string> = { UNDER: 'Few (under L)', RANGE: 'Middle', OVER: 'Many (over H)', EXACT: 'Exactly L or H', EXACT_LOW: 'Exactly L', EXACT_HIGH: 'Exactly H' };
const pct = (x: number, d = 1) => `${(x * 100).toFixed(d)} %`;
const ox = (x: number | null) => (x === null ? '—' : x.toFixed(2));

/**
 * Plain-language view of how fair the offered odds are, from settled live rounds: hit rate per selection
 * with its 95 % interval, the player's return per unit staked, the house edge, and odds that would give a
 * chosen margin. Observed results, not a model guarantee; the model section below is what real money needs.
 */
export function PricingInsights() {
  const { backend } = useCtx();
  const server = backend.server!;
  const [hours, setHours] = useState(168);
  const [margin, setMargin] = useState(8);
  const [v, setV] = useState<Insights | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await server.get<Insights>(`/v1/admin/pricing/insights?hours=${hours}&edgeBps=${Math.round(margin * 100)}`);
    if (r.ok) {
      setV(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server, hours, margin]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 0);
    return () => clearTimeout(t);
  }, [load]);
  const total = v ? v.outcome.below + v.outcome.inside + v.outcome.above : 0;
  return (
    <div className="pi" data-testid="pricing-insights">
      <Section title="Are the odds fair? (observed results)">
        <div className="pi-explain">
          <p>
            Every round sets two numbers from the camera&apos;s last minutes of traffic: <b>L</b> and <b>H</b>. The player bets on where the count lands. Each choice has a real chance; the <b>odds</b> pay back stake × odds when it wins. If a choice wins 40 % of the time and pays ×2.30, the player gets back 0.40 × 2.30 = <b>0.92 per 1 staked</b> on average — the house keeps 8 %.
          </p>
          <p className="muted">
            Odds now: <b>{v?.oddsSource === 'MODEL' ? 'from a validated model' : 'example odds (fixed, not yet fitted to data)'}</b>. Below, the real results show which choices pay too much (house edge below zero) and which odds would give the margin you pick. More rounds → narrower intervals.
          </p>
        </div>
        <div className="op-actions pi-controls">
          <div className="tm-status-btns" role="group" aria-label="Period">
            {[
              [24, '24 h'],
              [168, '7 days'],
              [720, '30 days'],
            ].map(([h, l]) => (
              <button key={h} type="button" className={hours === h ? 'on' : ''} onClick={() => setHours(h as number)}>
                {l}
              </button>
            ))}
          </div>
          <label className="pi-margin">
            Target house margin
            <input type="range" min={2} max={20} step={0.5} value={margin} onChange={(e) => setMargin(Number(e.target.value))} aria-label="Target house margin" />
            <b>{margin.toFixed(1)} %</b>
          </label>
        </div>
        {!v ? (
          <p className="muted">{err ?? 'Calculating…'}</p>
        ) : v.rounds === 0 ? (
          <p className="empty">No settled round in this period.</p>
        ) : (
          <>
            <p className="note">
              {v.rounds.toLocaleString()} settled rounds · intervals are 95 % (the true value is very likely inside).
            </p>
            <div className="table-wrap">
              <table className="pi-table">
                <thead>
                  <tr>
                    <th>Choice</th>
                    <th>Won</th>
                    <th>Odds offered</th>
                    <th>Player gets back per 1</th>
                    <th>House edge</th>
                    <th>Fair odds</th>
                    <th>Odds for {margin.toFixed(1)} % margin</th>
                  </tr>
                </thead>
                <tbody>
                  {v.selections.map((s) => {
                    const edge = 1 - s.playerReturn;
                    const sure = s.playerReturnCi[1] < 1 ? 'good' : s.playerReturnCi[0] > 1 ? 'bad' : 'unsure';
                    return (
                      <tr key={s.selection}>
                        <td>
                          <b>{LABEL[s.selection] ?? s.selection}</b>
                          <div className="muted mono">{s.selection}</div>
                        </td>
                        <td>
                          {pct(s.hitRate)} <span className="muted">({pct(s.hitRateCi[0], 0)}–{pct(s.hitRateCi[1], 0)})</span>
                          <div className="pi-bar" aria-hidden>
                            <i style={{ left: `${s.hitRateCi[0] * 100}%`, width: `${Math.max(0.5, (s.hitRateCi[1] - s.hitRateCi[0]) * 100)}%` }} />
                            <b style={{ left: `${s.hitRate * 100}%` }} />
                          </div>
                          <span className="muted small">
                            {s.hits}/{s.rounds}
                          </span>
                        </td>
                        <td className="mono">×{ox(s.avgOdds)}</td>
                        <td className="mono">
                          {s.playerReturn.toFixed(2)} <span className="muted">({s.playerReturnCi[0].toFixed(2)}–{s.playerReturnCi[1].toFixed(2)})</span>
                        </td>
                        <td>
                          <span className={`tag ${sure === 'good' ? 'st-SETTLED' : sure === 'bad' ? 'st-VOIDED' : 'st-PENDING'}`} title={sure === 'unsure' ? 'not enough rounds to be sure' : undefined}>
                            {pct(edge)}
                          </span>
                        </td>
                        <td className="mono">×{ox(s.fairOdds)}</td>
                        <td className="mono">
                          <b>×{ox(s.suggestedOdds)}</b>
                          {s.suggestedOdds !== null && Math.abs(s.suggestedOdds - s.avgOdds) / s.avgOdds > 0.05 && <span className={`muted small ${s.suggestedOdds < s.avgOdds ? 'pi-down' : 'pi-up'}`}> {s.suggestedOdds < s.avgOdds ? '▼ lower' : '▲ higher'}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="note">
              Green edge: the house keeps money on this choice (sure at 95 %). Red: it pays out more than it takes. Amber: not enough rounds to say. Suggested odds are a starting point from observed results — real-money odds come from a validated model per camera (below).
            </p>
            <h4>Where did the counts land?</h4>
            <div className="pi-split" aria-label="Count against the bands">
              {[
                ['below', 'under L', v.outcome.below],
                ['inside', 'between L and H', v.outcome.inside],
                ['above', 'over H', v.outcome.above],
              ].map(([k, l, n]) => (
                <span key={k as string} className={`pi-seg ${k as string}`} style={{ flex: Math.max(Number(n), 0.0001) }} title={`${l}: ${n}`}>
                  {total ? `${l} ${pct(Number(n) / total, 0)}` : ''}
                </span>
              ))}
            </div>
            {v.outcome.avgCountMinusMedian !== null && (
              <p className="note">
                On average the round&apos;s count was <b>{v.outcome.avgCountMinusMedian >= 0 ? '+' : ''}{v.outcome.avgCountMinusMedian.toFixed(2)}</b> vehicles against the median the bands were built from{Math.abs(v.outcome.avgCountMinusMedian) > 1 ? ' — the bands lag the traffic (rush hour or quieting roads).' : ' — the bands follow the traffic well.'}
              </p>
            )}
          </>
        )}
      </Section>
      {v && v.recent.length > 0 && (
        <Section title="Recent rounds: chance from the camera's last windows">
          <p className="note">For each round, the share of the camera&apos;s recent windows (the ones the bands came from) in which each choice would have won, and the edge that implies at the offered odds. Older rounds did not store the windows.</p>
          <div className="table-wrap">
            <table className="pi-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Camera</th>
                  <th>L / H</th>
                  <th>Count</th>
                  {['UNDER', 'RANGE', 'OVER', 'EXACT_LOW', 'EXACT_HIGH'].map((s) => (
                    <th key={s}>{LABEL[s]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {v.recent.map((r) => (
                  <tr key={r.gameRoundId}>
                    <td>
                      <Link to={`/rounds/${encodeURIComponent(r.gameRoundId)}`}>{r.gameRoundId}</Link>
                      <div className="muted small">{fmtTime(r.at)}</div>
                    </td>
                    <td>
                      {r.city ?? r.camera ?? '—'} <span className="muted">{r.country}</span>
                    </td>
                    <td className="mono">
                      {r.low} / {r.high}
                      {r.median !== null && <div className="muted small">median {r.median} · {r.windows} win.</div>}
                    </td>
                    <td className="mono">
                      <b>{r.finalCount}</b>
                    </td>
                    {['UNDER', 'RANGE', 'OVER', 'EXACT_LOW', 'EXACT_HIGH'].map((s) => {
                      const l = r.lines.find((x) => x.selection === s);
                      const won = r.winning.includes(s);
                      return (
                        <td key={s} className={`mono ${won ? 'pi-won' : ''}`}>
                          {l ? (
                            <>
                              ×{l.odds.toFixed(2)}
                              {l.p !== null && (
                                <div className="small">
                                  {pct(l.p, 0)} · <span className={l.edge! < 0 ? 'pi-neg' : 'pi-pos'}>{pct(l.edge!, 0)}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {v && (
        <Section title="Data towards a validated model (real money)">
          <p className="note">
            A camera can be priced by a model once it has at least {v.policy.minWindows} clean counting windows and {v.policy.minHourWindows}+ windows in each hour of the day it plays; then <code>pricing:train</code> fits and back-tests the model, and a PRICEABLE model replaces the example odds for that camera.
          </p>
          {v.readiness.length === 0 ? (
            <p className="empty">No counting windows collected yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="pi-table">
                <thead>
                  <tr>
                    <th>Camera</th>
                    <th>Windows</th>
                    <th>Hours of the day covered</th>
                    <th>Model</th>
                  </tr>
                </thead>
                <tbody>
                  {v.readiness.map((x) => (
                    <tr key={x.sourceKey}>
                      <td>
                        {x.city ?? x.camera ?? x.sourceKey} <span className="muted">{x.country}</span>
                      </td>
                      <td>
                        <div className="pi-progress" aria-label={`${x.windows} of ${v.policy.minWindows}`}>
                          <i style={{ width: `${Math.min(100, (x.windows / v.policy.minWindows) * 100)}%` }} />
                        </div>
                        <span className="small mono">
                          {x.windows} / {v.policy.minWindows}
                        </span>
                      </td>
                      <td>
                        <div className="pi-progress" aria-label={`${x.hoursOk} of 24 hours`}>
                          <i style={{ width: `${(x.hoursOk / 24) * 100}%` }} />
                        </div>
                        <span className="small mono">{x.hoursOk} / 24 h</span>
                      </td>
                      <td>{x.priceableModels ? <span className="tag st-SETTLED">PRICEABLE</span> : x.windows >= v.policy.minWindows ? <span className="tag st-PENDING">ready to train</span> : <span className="muted">collecting</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
